import { GlobalSettings, StartingPageOption } from './global.model';

/** Where users land when no starting page has been chosen in Settings -> File Library. */
export const DEFAULT_STARTING_PAGE: StartingPageOption = 'legacy-dashboard';

/** Route each starting page option resolves to. */
export const STARTING_PAGE_ROUTES: Readonly<Record<StartingPageOption, string>> = {
	'legacy-dashboard': '/',
	'file-library-home': '/dashboard',
	'file-library-projects': '/dashboard/projects'
};

export function isStartingPageOption(value: unknown): value is StartingPageOption {
	return value === 'legacy-dashboard' || value === 'file-library-projects' || value === 'file-library-home';
}

/**
 * The stored choice, ignoring the library toggle. For the settings UI only, which must keep
 * showing it while the library is off. Routing uses `resolveStartingPage()`.
 */
export function resolveConfiguredStartingPage(settings: Partial<GlobalSettings> | null | undefined): StartingPageOption {
	const configured = settings?.fileLibrarySettings?.startingPage;

	return isStartingPageOption(configured) ? configured : DEFAULT_STARTING_PAGE;
}

/** Who a starting page is resolved for. */
export interface StartingPageViewer {
	/**
	 * A signed-in user with no active non-Guest membership who is not a site admin
	 * (`SessionQuery.isGuestUser()`). Every library route carries `NoGuestGuard`, so guests never start there.
	 */
	guest: boolean;
}

/**
 * Where a user lands. Used by both routing guards and the post-login navigation.
 *
 * The library toggle is the gate: with it off the answer is always `legacy-dashboard`.
 * A guest always gets `legacy-dashboard`.
 */
export function resolveStartingPage(settings: Partial<GlobalSettings> | null | undefined, viewer: StartingPageViewer): StartingPageOption {
	if (viewer.guest || !settings?.fileLibrarySettings?.enableFileLibrary) {
		return 'legacy-dashboard';
	}

	return resolveConfiguredStartingPage(settings);
}

/** The route for `resolveStartingPage()`: `/`, `/dashboard` or `/dashboard/projects`. */
export function resolveStartingRoute(settings: Partial<GlobalSettings> | null | undefined, viewer: StartingPageViewer): string {
	return STARTING_PAGE_ROUTES[resolveStartingPage(settings, viewer)];
}
